import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface UserStats {
    username: string;
    totalProductions: number;
}

interface UserComparisonChartProps {
    data: UserStats[];
}

export const UserComparisonChart: FC<UserComparisonChartProps> = ({ data }) => {
    const { t } = useTranslation();

    // Veri kontrolü
    if (!data || data.length === 0) {
        return (
            <Card elevation={3} sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('aiStudioAnalytics.userComparison')}
                    </Typography>
                    <Box sx={{ width: '100%', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="textSecondary">{t('aiStudioAnalytics.noData')}</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    // Veri zaten camelCase formatında, direkt kullan
    const formattedData = data;

    return (
        <Card elevation={0} sx={{ mb: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: '24px !important' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
                    {t('aiStudioAnalytics.userComparison')}
                </Typography>
                <Box sx={{ width: '100%', height: 350 }}>
                    <ResponsiveContainer>
                        <BarChart data={formattedData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis 
                                dataKey="username" 
                                angle={-45} 
                                textAnchor="end" 
                                height={70} 
                                tickLine={false} 
                                axisLine={false}
                                style={{ fontSize: '0.75rem', fill: '#64748b', fontWeight: 500 }}
                            />
                            <YAxis 
                                tickLine={false} 
                                axisLine={false}
                                dx={-5}
                                style={{ fontSize: '0.75rem', fill: '#64748b', fontWeight: 500 }}
                            />
                            <Tooltip 
                                contentStyle={{ 
                                    backgroundColor: '#ffffff', 
                                    border: '1px solid #e2e8f0', 
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                    fontSize: '0.8125rem'
                                }} 
                            />
                            <Legend 
                                verticalAlign="top" 
                                height={36} 
                                iconType="circle"
                                iconSize={8}
                                wrapperStyle={{ fontSize: '0.8125rem', fontWeight: 500, color: '#64748b' }}
                            />
                            <Bar 
                                dataKey="totalProductions" 
                                fill="#1976d2" 
                                radius={[4, 4, 0, 0]}
                                name={t('aiStudioAnalytics.totalProductions')} 
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
};

